import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import api from "../../api";
import {
  tabOrder,
  ALL,
  FAILED_VALIDATION,
  PXDX,
  getRuleUnitConfigForTab,
  getRuleUnitTabSuffix,
} from "./Constants/PolicyTranslationConstants";
import { getTabLabels } from "./Utils/TranslationUtils";
import TopPanelComponent from "./TopPanelComponent";
import { TranslationFilter } from "./Filters/FiltersComponent";
import PolicyTabsComponent from "./PolicyTabs/PolicyTabsComponent";
import { Box } from "@mui/material";
import useGridResizeHook, {
  calculateGridHeight,
} from "hooks/useGridResizeHook";
import {
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Backdrop,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import RuleDetailsDialog from "./Dialogs/RuleDetailsDialog";
import PoetIcon from "components/PoetIcon/PoetIcon";
import ValidationErrorsDialog from "components/shared/Dialogs/ValidationErrorsDialog";
import { setDialog } from "slices/appSlice";
import { formErrorStrfromObj } from "utils/shared";
import Api from "api";

const RULE_EDIT_STATES = new Set(["NEW", "EDIT", "DELETE"]);
const RULE_UNIT_TYPE_KEY_BY_NAME = {
  PDM: 12,
};
const UNMAPPED_CODE_GROUP = "UNMAPPED_CODE_GROUP";
const COVERAGE_CRITERIA_MISSING_ERROR_CODE = "COVERAGE_CRITERIA_MISSING";
const CT_REVIEW_TYPE_MISMATCH_MESSAGE =
  "Incorrect CTReviewTypeName. Please contact system administrator";
const CODING_INCOMPLETE_ERROR_CODE = "CODING_INCOMPLETE";
const CODING_INCOMPLETE_VALIDATION_MESSAGE =
  "Error Message: Coding for policy must be complete prior to submitting rules";

/**
 * Resolves the required C&T review type from translation status.
 */
const getExpectedCtReviewType = (translationStatus = "") => {
  const normalizedStatus = String(translationStatus).trim().toLowerCase();
  if (normalizedStatus === "peer review") {
    return "Translation";
  }
  if (normalizedStatus === "pdc review") {
    return "PDC Translation";
  }
  return "";
};

/**
 * Returns true when the active C&T review type mismatches expected type.
 */
const hasCtReviewTypeMismatch = (reviewType = "", expectedReviewType = "") => {
  if (!expectedReviewType) {
    return false;
  }

  const normalizedReviewType = String(reviewType).trim();
  return normalizedReviewType !== expectedReviewType;
};

const toCodeGroupLabel = (group) => {
  if (!group) {
    return "";
  }

  if (group.codeGroupName && group.codeGroupVersion) {
    return `${group.codeGroupName} (ver ${group.codeGroupVersion})`;
  }

  return group.codeGroupName || group.codeGroupKey || "";
};

const mapRuleForGrid = (rule) => {
  const procedureGroups =
    rule.procedureCodeGroups ||
    rule.procedureGroups ||
    rule.procedureCodes ||
    [];
  const diagnosisGroups =
    rule.diagnosisCodeGroups ||
    rule.diagnosisGroups ||
    rule.diagnosisCodes ||
    [];

  return {
    ...rule,
    ruleName: rule.ruleName || rule.ruleUnitName || "",
    ruleUnitName: rule.ruleUnitName || rule.ruleName || "",
    cc: rule.cc || rule.changeControl || rule.coverageCriteriaList || "",
    version:
      rule.version ||
      (rule.ruleUnitVersion !== null && rule.ruleUnitVersion !== undefined
        ? String(rule.ruleUnitVersion)
        : ""),
    procedures:
      rule.procedures ||
      procedureGroups.map((group) => toCodeGroupLabel(group)).join(", "),
    ruleType:
      rule.ruleType ||
      rule.ruleUnitType?.ruleType ||
      rule.ruleUnitType?.sheetName ||
      "",
    diagnoses:
      rule.diagnoses !== undefined
        ? rule.diagnoses
        : diagnosisGroups.length > 0,
    rationale:
      rule.rationale !== undefined
        ? rule.rationale
        : Boolean(rule.ruleRationale || rule.rationaleText),
    procedureCodeGroups: procedureGroups,
    diagnosisCodeGroups: diagnosisGroups,
    editRuleUnit: rule.editRuleUnit || null,
  };
};

/**
 * Returns true when a C&T review row is still in-flight.
 */
const isIncompleteCtReviewRow = (row) =>
  row && !row.submittedDate && !row.completedDate;

/**
 * Picks the latest incomplete C&T review row for a given review type.
 */
const getLatestIncompleteCtReview = (rows = [], reviewType) => {
  const matchingRows = rows.filter(
    (row) =>
      isIncompleteCtReviewRow(row) &&
      (!reviewType || (row.reviewType || "") === reviewType)
  );

  if (matchingRows.length === 0) {
    return undefined;
  }

  return matchingRows.reduce((latest, current) => {
    const latestKey = Number(latest?.ctReviewKey || 0);
    const currentKey = Number(current?.ctReviewKey || 0);
    return currentKey >= latestKey ? current : latest;
  });
};

/**
 * Resolves line of business display name from policy LOB metadata.
 */
const resolveLineOfBusinessDisplayName = (
  lobConfigKey,
  lobDecisionList = []
) => {
  const normalizedKey = String(lobConfigKey ?? "");
  if (normalizedKey === "0") {
    return "Global";
  }

  const matchingLob = lobDecisionList.find(
    (lob) => String(lob?.id ?? "") === normalizedKey
  );

  return matchingLob?.lobDesc || null;
};

/**
 * Converts a date-like value to API DTO datetime format.
 */
const toDtoDateTime = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate = dayjs(value);
  if (!parsedDate.isValid()) {
    return null;
  }

  if (typeof value === "string" && value.includes("T")) {
    return parsedDate.format("YYYY-MM-DDTHH:mm:ss");
  }

  return parsedDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss");
};

const normalizeAdoptionConfigurations = (
  adoptionConfigurations,
  fallbackHealthPlanKey,
  adoptionDetails,
  lobDecisionList,
  includeUnmappedExistingAdoptions = false
) => {
  const normalizedAdoptionDetails = Array.isArray(adoptionDetails)
    ? adoptionDetails.filter((detail) => detail)
    : [];

  const normalizeExistingAdoptionConfiguration = (adoption) => {
    const lineOfBusinessConfigurationKey =
      adoption?.lineOfBusinessConfigurationKey === ""
        ? null
        : (adoption?.lineOfBusinessConfigurationKey ?? null);

    return {
      adoptionConfigurationKey: adoption?.adoptionConfigurationKey,
      healthPlanKey:
        adoption?.healthPlanKey === "" || adoption?.healthPlanKey === undefined
          ? (fallbackHealthPlanKey ?? null)
          : adoption.healthPlanKey,
      lineOfBusinessConfigurationKey,
      lineOfBusinessDisplayName: resolveLineOfBusinessDisplayName(
        lineOfBusinessConfigurationKey,
        lobDecisionList
      ),
      effectiveDateTime:
        adoption?.effectiveDateTime || toDtoDateTime(adoption?.effectiveDate),
      endDateTime:
        adoption?.endDateTime || toDtoDateTime(adoption?.endDate) || null,
    };
  };

  const getAdoptionIdentity = (adoption) => {
    if (adoption?.adoptionConfigurationKey !== undefined) {
      return `key:${adoption.adoptionConfigurationKey}`;
    }

    return [
      "lob",
      String(adoption?.lineOfBusinessConfigurationKey ?? ""),
      String(adoption?.effectiveDateTime ?? ""),
      String(adoption?.effectiveDate ?? ""),
    ].join(":");
  };

  const dedupeNormalizedAdoptionConfigurations = (items = []) => {
    const seenKeys = new Set();
    const seenLobKeys = new Set();

    return items.filter((item) => {
      const hasAdoptionConfigurationKey =
        item?.adoptionConfigurationKey !== undefined &&
        item?.adoptionConfigurationKey !== null;

      if (hasAdoptionConfigurationKey) {
        const key = String(item.adoptionConfigurationKey);
        if (seenKeys.has(key)) {
          return false;
        }

        seenKeys.add(key);
        return true;
      }

      const lobKey = String(item?.lineOfBusinessConfigurationKey ?? "");
      if (seenLobKeys.has(lobKey)) {
        return false;
      }

      seenLobKeys.add(lobKey);
      return true;
    });
  };

  const findMatchingAdoptionConfiguration = (detail, index) => {
    if (!Array.isArray(adoptionConfigurations)) {
      return undefined;
    }

    const detailKey = String(detail?.LobConfigKey ?? "");
    const keyMatch = adoptionConfigurations.find(
      (adoption) =>
        String(adoption?.lineOfBusinessConfigurationKey ?? "") === detailKey
    );

    if (keyMatch) {
      return keyMatch;
    }

    return adoptionConfigurations[index];
  };

  if (normalizedAdoptionDetails.length > 0) {
    const normalizedFromDetails = normalizedAdoptionDetails.map(
      (detail, index) => {
        const matchedAdoptionConfiguration = findMatchingAdoptionConfiguration(
          detail,
          index
        );

        return {
          adoptionConfigurationKey:
            matchedAdoptionConfiguration?.adoptionConfigurationKey,
          healthPlanKey:
            matchedAdoptionConfiguration?.healthPlanKey === "" ||
            matchedAdoptionConfiguration?.healthPlanKey === undefined
              ? (fallbackHealthPlanKey ?? null)
              : matchedAdoptionConfiguration.healthPlanKey,
          lineOfBusinessConfigurationKey:
            detail?.LobConfigKey === "" ? null : (detail?.LobConfigKey ?? null),
          lineOfBusinessDisplayName: resolveLineOfBusinessDisplayName(
            detail?.LobConfigKey,
            lobDecisionList
          ),
          effectiveDateTime:
            matchedAdoptionConfiguration?.effectiveDateTime ||
            toDtoDateTime(detail?.effectiveDate),
          endDateTime:
            detail?.endDate === ""
              ? null
              : toDtoDateTime(detail?.endDate) ||
                matchedAdoptionConfiguration?.endDateTime ||
                null,
        };
      }
    );

    if (
      includeUnmappedExistingAdoptions &&
      Array.isArray(adoptionConfigurations) &&
      adoptionConfigurations.length > 0
    ) {
      const existingIds = new Set(
        normalizedFromDetails.map(getAdoptionIdentity)
      );
      const unmatchedExisting = adoptionConfigurations
        .filter((adoption) => !existingIds.has(getAdoptionIdentity(adoption)))
        .map((adoption) => normalizeExistingAdoptionConfiguration(adoption));

      return dedupeNormalizedAdoptionConfigurations([
        ...normalizedFromDetails,
        ...unmatchedExisting,
      ]);
    }

    return dedupeNormalizedAdoptionConfigurations(normalizedFromDetails);
  }

  if (
    !Array.isArray(adoptionConfigurations) ||
    adoptionConfigurations.length === 0
  ) {
    return [
      {
        healthPlanKey: fallbackHealthPlanKey ?? null,
        lineOfBusinessConfigurationKey: null,
      },
    ];
  }

  return dedupeNormalizedAdoptionConfigurations(
    adoptionConfigurations.map((adoption) =>
      normalizeExistingAdoptionConfiguration(adoption)
    )
  );
};

/**
 * Builds validation payload while preserving do-not-adopt adoption rows.
 */
const buildRuleUnitValidationPayload = (
  rule,
  healthPlanKey,
  lobDecisionList
) => {
  const { adoptionDetails, ...rulePayload } = rule;

  return {
    ...rulePayload,
    adoptionConfigurations: normalizeAdoptionConfigurations(
      rule.adoptionConfigurations,
      healthPlanKey,
      adoptionDetails,
      lobDecisionList,
      true
    ),
  };
};

const buildRuleUnitSavePayload = (
  rule,
  policyKey,
  healthPlanKey,
  lobDecisionList
) => {
  const editRuleUnit = (rule.editRuleUnit || "").toUpperCase();
  const adoptionConfigurations = normalizeAdoptionConfigurations(
    rule.adoptionConfigurations,
    healthPlanKey,
    rule.adoptionDetails,
    lobDecisionList,
    true
  );
  const ruleUnitTypeName =
    typeof rule.ruleUnitType === "string"
      ? rule.ruleUnitType.toUpperCase()
      : null;
  const resolvedRuleUnitTypeKey =
    rule.ruleUnitType?.ruleUnitTypeKey ||
    RULE_UNIT_TYPE_KEY_BY_NAME[ruleUnitTypeName] ||
    null;
  const { adoptionDetails, ...rulePayload } = rule;

  if (editRuleUnit === "DELETE") {
    return {
      ruleUnitKey: rule.ruleUnitKey,
      policyKey,
      adoptionConfigurations,
      editRuleUnit,
    };
  }

  return {
    ...rulePayload,
    ruleUnitKey: rule.ruleUnitKey || null,
    policyKey,
    ruleUnitId: rule.ruleUnitId || null,
    isActive: rule.isActive ?? true,
    isVariance: rule.isVariance ?? false,
    ruleUnitVersion: rule.ruleUnitVersion || null,
    ruleUnitType: resolvedRuleUnitTypeKey
      ? { ruleUnitTypeKey: resolvedRuleUnitTypeKey }
      : null,
    ruleUnitName: rule.ruleUnitName || rule.ruleName || "",
    ruleDescription: rule.ruleDescription || "",
    ruleRationale: rule.ruleRationale || rule.rationaleText || "",
    changeControl: rule.changeControl || rule.cc || "",
    coverageCriteriaList:
      rule.coverageCriteriaList || rule.coverageCriteria || rule.cc || "",
    adoptionConfigurations,
    dxLimitation: rule.dxLimitation || null,
    procedureCodeGroups: rule.procedureCodeGroups || rule.procedureGroups || [],
    diagnosisCodeGroups: rule.diagnosisCodeGroups || rule.diagnosisGroups || [],
    editRuleUnit,
  };
};

const getAllowedRuleUnitTypesForTab = (tabValue) => {
  if (tabValue === null || tabValue === undefined) {
    return null;
  }

  const ruleConfig = getRuleUnitConfigForTab(tabValue);
  if (!ruleConfig?.ruleUnitType) {
    return [];
  }

  return [String(ruleConfig.ruleUnitType).toUpperCase()];
};

const getRuleUnitTypeValue = (rule) => {
  const ruleUnitType = rule?.ruleUnitType;

  if (typeof ruleUnitType === "string") {
    return ruleUnitType.toUpperCase();
  }

  if (ruleUnitType?.ruleType) {
    return String(ruleUnitType.ruleType).toUpperCase();
  }

  if (ruleUnitType?.sheetName) {
    return String(ruleUnitType.sheetName).toUpperCase();
  }

  if (rule?.ruleType) {
    return String(rule.ruleType).toUpperCase();
  }

  return "";
};

const resolveRuleUnitLabel = (ruleUnitKey, rules = []) => {
  const normalizedKey = String(ruleUnitKey || "");
  const matchingRule = rules.find(
    (rule) =>
      String(rule?.ruleUnitKey || "") === normalizedKey ||
      String(rule?.ruleUnitId || "") === normalizedKey
  );

  if (!matchingRule) {
    return `Rule Unit ${normalizedKey}`;
  }

  return (
    matchingRule.ruleUnitName ||
    matchingRule.ruleName ||
    `Rule Unit ${normalizedKey}`
  );
};

const buildRuleUnitLobValidationMessages = (
  ruleUnitValidationErrors = {},
  rules = [],
  lobDecisionList = []
) =>
  Object.entries(ruleUnitValidationErrors).flatMap(
    ([ruleUnitKey, validation]) => {
      const ruleLabel = resolveRuleUnitLabel(ruleUnitKey, rules);
      const lobErrors = Array.isArray(validation?.lobErrors)
        ? validation.lobErrors
        : [];

      if (lobErrors.length === 0) {
        return [];
      }

      return lobErrors.map((lobError) => {
        const lobLabel = resolveLineOfBusinessDisplayName(
          lobError?.LobConfigKey,
          lobDecisionList
        );
        return lobLabel
          ? `${ruleLabel}: ${lobLabel} - ${lobError.message}`
          : `${ruleLabel}: ${lobError.message}`;
      });
    }
  );

const buildGeneralValidationMessages = (generalValidationErrors = {}) =>
  Object.values(generalValidationErrors)
    .flatMap((value) => {
      if (Array.isArray(value)) {
        return value;
      }

      if (value && typeof value === "object") {
        return Object.values(value);
      }

      return value ? [value] : [];
    })
    .filter(Boolean);

/**
 * Resolves a human-readable code group label from rules and policy state.
 */
const resolveCodeGroupLabel = (codeGroupKey, rules = [], policyState) => {
  const normalizedKey = String(codeGroupKey || "");

  const allRuleCodeGroups = rules.flatMap((rule) => [
    ...(rule.procedureCodeGroups || []),
    ...(rule.diagnosisCodeGroups || []),
  ]);

  const matchingRuleCodeGroup = allRuleCodeGroups.find(
    (group) => String(group?.codeGroupKey || "") === normalizedKey
  );

  if (matchingRuleCodeGroup) {
    return toCodeGroupLabel(matchingRuleCodeGroup);
  }

  const matchingPolicyCodeGroup = (policyState?.codeGroups || []).find(
    (group) => String(group?.codeGroupKey || "") === normalizedKey
  );

  if (matchingPolicyCodeGroup) {
    return toCodeGroupLabel(matchingPolicyCodeGroup);
  }

  return `Code Group ${normalizedKey}`;
};

/**
 * Detects the backend CODING_INCOMPLETE error from formatted error text.
 */
const isCodingIncompleteError = (error) => {
  const formattedMessage = formErrorStrfromObj(error) || "";
  return formattedMessage.includes(CODING_INCOMPLETE_ERROR_CODE);
};

/**
 * Builds the dialog message for a validation error entry.
 */
const getRuleUnitValidationMessage = (
  codeGroupKey,
  rules,
  policyState,
  code,
  validation
) => {
  if (code === COVERAGE_CRITERIA_MISSING_ERROR_CODE) {
    const missingCoverageCriteria = validation?.missingCoverageCriteria ?? "";

    return `Coverage Criteria ${missingCoverageCriteria} is not tied to any active Rules.`;
  }

  const label = resolveCodeGroupLabel(codeGroupKey, rules, policyState);

  return `${label} is not tied to any active Rules. Do you want to remove the code group or update rules?`;
};

const PolicyTranslationPostOHR = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const topPanelRef = useRef(null);
  const [gridHeight, setGridHeight] = useState("auto");
  const policyState = location?.state?.policyState;
  const { policyNumber } = location.state;
  const { policyTitle } = location.state;
  const healthPlanKey = policyState?.healthPlanKey;
  const healthPlanId = policyState?.healthPlanId;
  const policyKey = policyState?.policyKey;
  const editable = policyState.editMode || false;
  const [activeInactive, setActiveInactive] = useState(ALL);
  const [rules, setRules] = useState([]);
  const [reviewData, setReviewData] = useState({
    ctReviewKey: undefined,
    results: "",
    comments: "",
    reviewType: "",
  });
  const [selectedValidation, setSelectedValidation] = useState(ALL);
  const [tabValue, setTabValue] = useState(0);
  const [dynamicTabs, setDynamicTabs] = useState(getTabLabels());
  const [selectedRule, setSelectedRule] = useState(null);
  const [policyData, setPolicyData] = useState({
    policyTranslator: "",
    translationReviewer: "",
    translationStatus: "",
    reviewType: "",
    overallAdoptionDecision: "",
    lobAdoptionDecisionList: [],
    policyChangeControlXrefDto: {
      policyChangeControlXrefKey: null,
      changeControlText: "",
    },
  });
  const [ctReviewData, setCTReviewData] = useState([]);
  const [policyHistoryData, setPolicyHistoryData] = useState([]);
  const [ruleUnitValidationDialogOpen, setRuleUnitValidationDialogOpen] =
    useState(false);
  const [ruleUnitValidationMessages, setRuleUnitValidationMessages] = useState(
    []
  );
  const [unmappedCodeGroupKeys, setUnmappedCodeGroupKeys] = useState([]);
  const [showTranslationReviewerError, setShowTranslationReviewerError] =
    useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [pendingValidationErrors, setPendingValidationErrors] = useState([]);

  const [loading, setLoading] = useState(false);
  const lobAdoptionDecisionList = policyData.lobAdoptionDecisionList || [];

  const filteredRules = useMemo(() => {
    const allowedRuleUnitTypes = getAllowedRuleUnitTypesForTab(tabValue);

    if (allowedRuleUnitTypes === null) {
      return rules;
    }

    return rules.filter((rule) =>
      allowedRuleUnitTypes.includes(getRuleUnitTypeValue(rule))
    );
  }, [rules, tabValue]);

  const resetFilter = () => {
    console.log("Resetting filters");
  };

  const closeRuleUnitValidationDialog = useCallback(() => {
    setRuleUnitValidationDialogOpen(false);
    setUnmappedCodeGroupKeys([]);

    if (pendingValidationErrors.length > 0) {
      setValidationErrors(pendingValidationErrors);
      setValidationDialogOpen(true);
      setPendingValidationErrors([]);
    }
  }, [pendingValidationErrors]);

  const dynamicMenuItems = dynamicTabs.filter((t) => !t.display);

  const handleCancelAction = () => {
    navigate(-1);
  };

  const resizeEvent = useCallback(
    () => calculateGridHeight(topPanelRef, setGridHeight, 40),
    [topPanelRef, setGridHeight]
  );

  useGridResizeHook(resizeEvent);
  const setTopPanelEl = useCallback(
    (el) => {
      topPanelRef.current = el;
      if (el) {
        resizeEvent();
      }
    },
    [resizeEvent]
  );

  const handleAccordionToggle = () => {
    setTimeout(() => {
      resizeEvent();
    }, 400);
  };

  const openPlusTab = (event) => {
    setPlusTabAnchor(event.currentTarget);
  };

  const saveDirtyRuleUnits = useCallback(
    async (currentTabValue) => {
      const dirtyRules = rules.filter((rule) =>
        RULE_EDIT_STATES.has((rule.editRuleUnit || "").toUpperCase())
      );

      if (dirtyRules.length === 0) {
        return true;
      }

      try {
        const tabSuffix = getRuleUnitTabSuffix(currentTabValue);

        if (!tabSuffix) {
          return true;
        }

        const payload = dirtyRules.map((rule) =>
          buildRuleUnitSavePayload(
            rule,
            policyKey,
            healthPlanKey,
            lobAdoptionDecisionList
          )
        );
        await Api.policyData.updatePolicyRuleUnits(
          healthPlanKey,
          policyKey,
          tabSuffix,
          payload
        );

        setRules((prevRules) =>
          prevRules.map((rule) => {
            const editRuleUnit = (rule.editRuleUnit || "").toUpperCase();
            if (!RULE_EDIT_STATES.has(editRuleUnit)) {
              return rule;
            }

            return {
              ...rule,
              editRuleUnit: null,
            };
          })
        );
        return true;
      } catch (error) {
        const message =
          formErrorStrfromObj(error) || "The policy could not be saved.";
        dispatch(setDialog({ message, title: "Error" }));
        return false;
      }
    },
    [dispatch, healthPlanKey, lobAdoptionDecisionList, policyKey, rules]
  );

  const loadRuleUnitsForTab = useCallback(
    async (nextTabValue) => {
      try {
        const tabSuffix = getRuleUnitTabSuffix(nextTabValue);

        if (!tabSuffix) {
          setRules([]);
          return true;
        }

        const response = await Api.policyData.getPolicyRuleUnits(
          healthPlanKey,
          policyKey,
          tabSuffix
        );
        const groupedRules = response?.data || {};
        const loadedRules = Object.values(groupedRules).flat();
        setRules(loadedRules.map(mapRuleForGrid));
        return true;
      } catch (error) {
        const message =
          formErrorStrfromObj(error) || "Unable to load rule units.";
        dispatch(setDialog({ message, title: "Error" }));
        return false;
      }
    },
    [dispatch, healthPlanKey, policyKey]
  );

  const changeTab = useCallback(
    async (nextTabValue) => {
      if (nextTabValue === tabValue) {
        return true;
      }

      setLoading(true);
      const saveSuccess = await saveDirtyRuleUnits(tabValue);
      if (!saveSuccess) {
        setLoading(false);
        return false;
      }

      const loadSuccess = await loadRuleUnitsForTab(nextTabValue);
      if (loadSuccess) {
        setTabValue(nextTabValue);
      }
      setLoading(false);
      return loadSuccess;
    },
    [loadRuleUnitsForTab, saveDirtyRuleUnits, tabValue]
  );

  const handleTabChange = async (event, newValue) => {
    await changeTab(newValue);
  };

  const [plusTabAnchor, setPlusTabAnchor] = useState("");

  const addTab = async (tabKey) => {
    const tabIndex = tabOrder.get(tabKey);
    const dynamicTabsIndex = dynamicTabs.findIndex((t) => t.key === tabKey);
    setDynamicTabs(
      dynamicTabs.toSpliced(dynamicTabsIndex, 1, {
        key: tabKey,
        length: 0,
        display: true,
      })
    );

    await changeTab(tabIndex);
    closePlusTab();
  };

  const closePlusTab = () => {
    setPlusTabAnchor("");
  };

  const tabColor = (length) =>
    selectedValidation === FAILED_VALIDATION && length > 0
      ? "red"
      : "primary.main";

  const description = useMemo(() => {
    const expectedCtReviewType = getExpectedCtReviewType(
      policyData.translationStatus
    );
    return expectedCtReviewType || "Translation";
  }, [policyData.translationStatus]);

  const derivedReviewData = useMemo(() => {
    const matchingRow = getLatestIncompleteCtReview(ctReviewData, description);
    return {
      reviewType: description,
      ctReviewKey: matchingRow?.ctReviewKey,
      results: matchingRow?.results || "",
      comments: matchingRow?.comments || "",
    };
  }, [ctReviewData, description]);

  const effectiveReviewData = useMemo(
    () => ({
      reviewType: reviewData.reviewType || derivedReviewData.reviewType,
      ctReviewKey:
        reviewData.ctReviewKey !== undefined
          ? reviewData.ctReviewKey
          : derivedReviewData.ctReviewKey,
      results:
        reviewData.results !== undefined && reviewData.results !== ""
          ? reviewData.results
          : derivedReviewData.results,
      comments:
        reviewData.comments !== undefined
          ? reviewData.comments
          : derivedReviewData.comments,
    }),
    [reviewData, derivedReviewData]
  );

  // State for RuleDetailsDialog
  const [ruleDetailsOpen, setRuleDetailsOpen] = useState(false);
  const [ruleDetailsTitle, setRuleDetailsTitle] = useState("");

  /**
   * Validates rule units on submit and blocks submit when unmapped code groups exist.
   */
  const validateRuleUnitsOnSubmit = useCallback(async () => {
    try {
      const tabSuffix = getRuleUnitTabSuffix(tabValue);

      if (!tabSuffix) {
        return true;
      }

      const validationPayload = filteredRules.map((rule) =>
        buildRuleUnitValidationPayload(
          rule,
          healthPlanKey,
          lobAdoptionDecisionList
        )
      );
      const response = await Api.policyData.validatePolicyRuleUnits(
        policyState?.policyKey,
        tabSuffix,
        validationPayload
      );
      const validationData = response?.data || {};
      const codeGroupValidationErrors =
        validationData.codeGroupValidationErrors || {};
      const varianceLobValidationErrors =
        validationData.varianceLobValidationErrors || {};
      const ruleUnitValidationErrors =
        validationData.ruleUnitValidationErrors || {};
      const generalValidationErrors =
        validationData.generalValidationErrors || {};
      const validationEntries = Object.entries(codeGroupValidationErrors);
      const unmappedCodeGroups = validationEntries.filter(
        ([, validation]) => validation?.code === UNMAPPED_CODE_GROUP
      );
      const coverageCriteriaMissing = validationEntries.filter(
        ([, validation]) =>
          validation?.code === COVERAGE_CRITERIA_MISSING_ERROR_CODE
      );
      const ruleUnitMessages = buildRuleUnitLobValidationMessages(
        ruleUnitValidationErrors,
        rules,
        lobAdoptionDecisionList
      );
      const varianceMessages = Object.values(varianceLobValidationErrors)
        .map((varianceValidationError) => varianceValidationError?.message)
        .filter(Boolean);
      const generalMessages = buildGeneralValidationMessages(
        generalValidationErrors
      );

      const codeGroupMessages = [
        ...unmappedCodeGroups.map(([codeGroupKey]) =>
          getRuleUnitValidationMessage(
            codeGroupKey,
            rules,
            policyState,
            UNMAPPED_CODE_GROUP,
            undefined
          )
        ),
        ...coverageCriteriaMissing.map(([codeGroupKey, validation]) =>
          getRuleUnitValidationMessage(
            codeGroupKey,
            rules,
            policyState,
            COVERAGE_CRITERIA_MISSING_ERROR_CODE,
            validation
          )
        ),
      ];

      const otherValidationMessages = [
        ...varianceMessages,
        ...ruleUnitMessages,
        ...generalMessages,
      ];

      if (
        codeGroupMessages.length === 0 &&
        otherValidationMessages.length === 0
      ) {
        setUnmappedCodeGroupKeys([]);
        setRuleUnitValidationMessages([]);
        setRuleUnitValidationDialogOpen(false);
        setPendingValidationErrors([]);
        setValidationErrors([]);
        setValidationDialogOpen(false);
        return true;
      }

      if (otherValidationMessages.length > 0) {
        setPendingValidationErrors(otherValidationMessages);
      } else {
        setPendingValidationErrors([]);
      }

      if (codeGroupMessages.length === 0) {
        setUnmappedCodeGroupKeys([]);
        setRuleUnitValidationMessages([]);
        setRuleUnitValidationDialogOpen(false);
        setValidationErrors(otherValidationMessages);
        setValidationDialogOpen(true);
        return false;
      }

      setUnmappedCodeGroupKeys(
        unmappedCodeGroups.map(([codeGroupKey]) => codeGroupKey)
      );
      setRuleUnitValidationMessages(codeGroupMessages);
      setRuleUnitValidationDialogOpen(true);
      return false;
    } catch (error) {
      const message =
        formErrorStrfromObj(error) || "Unable to validate policy rules.";
      dispatch(setDialog({ message, title: "Error" }));
      return false;
    }
  }, [
    dispatch,
    filteredRules,
    healthPlanKey,
    lobAdoptionDecisionList,
    policyState,
    rules,
    tabValue,
  ]);

  useEffect(() => {
    const fetchPolicyData = async () => {
      setLoading(true);
      try {
        const response = await api.policyData.getPolicyData(
          healthPlanKey,
          policyKey
        );
        if (response.status === 200 && response.data != null) {
          setPolicyData({
            policyTranslator: response.data.policyTranslator,
            translationReviewer: response.data.translationReviewer,
            translationStatus: response.data.translationStatus,
            reviewType: response.data.reviewType,
            overallAdoptionDecision: response.data.overallAdoptionDecision
              ? response.data.overallAdoptionDecision
              : "",
            lobAdoptionDecisionList: response.data.lobAdoptionDecisionList
              ? response.data.lobAdoptionDecisionList
              : [],
            policyChangeControlXrefDto:
              response.data.policyChangeControlXrefDto,
          });
          setPolicyHistoryData(response.data.policyHistoryList);
          setCTReviewData(response.data.policyCTReviewList);
        } else if (response.data == null) {
          throw new Error("API failed to return policy data.");
        }
      } catch (e) {
        const message = formErrorStrfromObj(e) || "Unable to load policy data.";
        dispatch(setDialog({ message, title: "Error" }));
      } finally {
        setLoading(false);
      }
    };
    if (healthPlanKey && policyKey) fetchPolicyData();
  }, [healthPlanKey, policyKey]);

  useEffect(() => {
    const fetchRuleUnits = async () => {
      setLoading(true);
      await loadRuleUnitsForTab(tabOrder.get(PXDX));
      setLoading(false);
    };

    if (healthPlanKey && policyKey) {
      fetchRuleUnits();
    }
  }, [healthPlanKey, loadRuleUnitsForTab, policyKey]);

  /**
   * Saves translation policy data and forwards submit intent to the API layer.
   */

  const updatePolicyData = useCallback(
    async (status, isSubmit = false) => {
      if (!policyState?.policyKey) {
        const message = "Missing policy key. Unable to save policy.";
        dispatch(setDialog({ message, title: "Error" }));
        return;
      }

      const normalizedTranslationReviewer = String(
        policyData.translationReviewer || ""
      ).trim();

      setValidationErrors([]);
      setValidationDialogOpen(false);

      if (isSubmit && !normalizedTranslationReviewer) {
        setShowTranslationReviewerError(true);
        return;
      }
      setShowTranslationReviewerError(false);

      const normalizedTranslationStatus = (
        policyData.translationStatus || ""
      ).toLowerCase();
      const requiresCtReviewValidation =
        isSubmit &&
        (normalizedTranslationStatus === "peer review" ||
          normalizedTranslationStatus === "pdc review");
      const expectedCtReviewType = getExpectedCtReviewType(
        policyData.translationStatus
      );

      if (
        requiresCtReviewValidation &&
        hasCtReviewTypeMismatch(
          effectiveReviewData?.reviewType,
          expectedCtReviewType
        )
      ) {
        setValidationErrors([CT_REVIEW_TYPE_MISMATCH_MESSAGE]);
        setValidationDialogOpen(true);
        return;
      }

      if (
        requiresCtReviewValidation &&
        (!effectiveReviewData?.reviewType || !effectiveReviewData?.results)
      ) {
        dispatch(
          setDialog({
            message: "C&T Review is required.",
            title: "Validation Failure",
          })
        );
        return;
      }

      if (
        requiresCtReviewValidation &&
        effectiveReviewData?.results === "fail" &&
        !effectiveReviewData?.comments?.trim()
      ) {
        dispatch(
          setDialog({
            message: "Comments are required when C&T Reviews Result is 'Fail'.",
            title: "Validation Failure",
          })
        );
        return;
      }

      setLoading(true);

      const rulesSaved = await saveDirtyRuleUnits(tabValue);
      if (!rulesSaved) {
        setLoading(false);
        return;
      }

      if (isSubmit && filteredRules.length > 0) {
        const validationPassed = await validateRuleUnitsOnSubmit();
        if (!validationPassed) {
          setLoading(false);
          return;
        }
      }

      const payload = {
        policyKey: policyState?.policyKey,
        policyStage: "Translation",
        policyTranslator: policyData.policyTranslator || null,
        translationReviewer: normalizedTranslationReviewer || null,
        policyChangeControlXrefDto: policyData.policyChangeControlXrefDto,
        policyHistoryData,
        ctReviewData: effectiveReviewData,
      };

      try {
        await Api.policyData.updatePolicyData(
          healthPlanKey,
          policyState?.policyKey,
          payload,
          isSubmit
        );

        // translationStatus updated by backend response

        navigate("/policies-active", {
          state: {
            saveStatus: status,
          },
        });
      } catch (error) {
        if (isSubmit && isCodingIncompleteError(error)) {
          setValidationErrors([CODING_INCOMPLETE_VALIDATION_MESSAGE]);
          setValidationDialogOpen(true);
        } else {
          const message =
            formErrorStrfromObj(error) || "The policy could not be saved.";
          dispatch(setDialog({ message, title: "Error" }));
        }
      } finally {
        setLoading(false);
      }
    },
    [
      dispatch,
      healthPlanKey,
      navigate,
      policyData,
      policyHistoryData,
      policyState,
      filteredRules,
      rules,
      ctReviewData,
      effectiveReviewData,
      saveDirtyRuleUnits,
      tabValue,
      validateRuleUnitsOnSubmit,
    ]
  );

  const handleAddNewRule = (rule) => {
    setRuleDetailsTitle("");
    setSelectedRule(null);
    setRuleDetailsOpen(true);
  };

  // Handler to open dialog from RuleGrid
  const handleEditNoteClick = (rule) => {
    setRuleDetailsTitle(rule.ruleUnitName || rule.ruleName || "Rule Details");
    setSelectedRule(rule);
    setRuleDetailsOpen(true);
  };
  const handleRuleDetailsClose = () => {
    setRuleDetailsOpen(false);
    setSelectedRule(null);
  };

  const handleAddRule = (rule) => {
    setRules((prevRules) => {
      if (rule.ruleUnitKey) {
        const hasMatch = prevRules.some(
          (existingRule) => existingRule.ruleUnitKey === rule.ruleUnitKey
        );

        if (hasMatch) {
          return prevRules.map((existingRule) => {
            if (existingRule.ruleUnitKey !== rule.ruleUnitKey) {
              return existingRule;
            }

            return {
              ...existingRule,
              ...rule,
              editRuleUnit:
                (existingRule.editRuleUnit || "").toUpperCase() === "NEW"
                  ? "NEW"
                  : "EDIT",
            };
          });
        }
      }

      return [...prevRules, mapRuleForGrid(rule)];
    });
    setRuleDetailsOpen(false);
    setSelectedRule(null);
  };
  const handleCreateVariance = (rule) => {
    setRules((prevRules) => [...prevRules, mapRuleForGrid(rule)]);
    setSelectedRule(rule);
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box ref={setTopPanelEl} className="top-half" sx={{ flex: "none" }}>
        <TopPanelComponent
          resetFilter={resetFilter}
          policyStateData={policyState}
          healthPlanDisplayName={policyState?.healthPlanDisplayName || ""}
          policyNumber={policyNumber}
          policyTitle={policyTitle}
          editable={editable}
          healthPlanKey={"-1"}
          policyKey={policyState?.policyKey}
          handleCancelAction={handleCancelAction}
          sx={{ mb: 4 }}
          policyHistoryData={policyHistoryData}
          setPolicyHistoryData={setPolicyHistoryData}
          policyVersion={location?.state?.policyVersion || ""}
          policyData={policyData}
          setPolicyData={setPolicyData}
          reviewData={effectiveReviewData}
          setReviewData={setReviewData}
          ctReviewData={ctReviewData}
          updatePolicyData={updatePolicyData}
          showTranslationReviewerError={showTranslationReviewerError}
          onTranslationReviewerChange={(value) => {
            if (value && String(value).trim()) {
              setShowTranslationReviewerError(false);
            }
          }}
        >
          <TranslationFilter
            setActiveInactive={setActiveInactive}
            activeInactive={activeInactive}
            onAccordionToggle={handleAccordionToggle}
          />

          <Box
            sx={{
              display: "flex",
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& button": {
                  textTransform: "none",
                },
              }}
            >
              {dynamicTabs.map(
                (tab) =>
                  tab.display && (
                    <Tab
                      sx={{
                        color: tabColor(tab.length),
                        [`&.Mui-selected`]: {
                          color: tabColor(tab.length),
                        },
                      }}
                      key={tab.key}
                      value={tabOrder.get(tab.key)}
                      label={`${tab.key} (${tab.length})`}
                    />
                  )
              )}
            </Tabs>
            {dynamicMenuItems.length !== 0 && (
              <IconButton
                data-testid="add-translation-tab"
                onClick={openPlusTab}
              >
                <PoetIcon iconName="medical-files" />
              </IconButton>
            )}
            <Menu
              anchorEl={plusTabAnchor}
              onClose={closePlusTab}
              open={Boolean(plusTabAnchor)}
            >
              {dynamicMenuItems.map(({ key }) => {
                return (
                  <MenuItem key={key} onClick={() => addTab(key)}>
                    {key}
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </TopPanelComponent>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {tabValue !== -1 && (
          <PolicyTabsComponent
            tabValue={tabValue}
            onEditNoteClick={handleEditNoteClick}
            handleAddNewRule={handleAddNewRule}
            editable={editable}
            policyData={policyData}
            healthPlanId={healthPlanId}
            rules={rules}
            setRules={setRules}
          />
        )}
      </Box>
      <RuleDetailsDialog
        open={ruleDetailsOpen}
        onClose={handleRuleDetailsClose}
        title={ruleDetailsTitle}
        tabValue={tabValue}
        policyState={policyState}
        healthPlanKey={healthPlanKey}
        setLoading={setLoading}
        onAddRule={handleAddRule}
        onCreateVariance={handleCreateVariance}
        selectedRule={selectedRule}
        rules={rules}
        readOnly={!(editable && !selectedRule?.immutable)}
        canCreateVariance={editable}
      >
        {/* You can add more details here if needed */}
      </RuleDetailsDialog>
      <Dialog
        open={ruleUnitValidationDialogOpen}
        onClose={closeRuleUnitValidationDialog}
      >
        <DialogTitle>Code Group Validation</DialogTitle>
        <DialogContent>
          {ruleUnitValidationMessages.map((message) => (
            <Box key={message} sx={{ mb: 1 }}>
              {message}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          {unmappedCodeGroupKeys.length > 0 && (
            <Button
              variant="contained"
              onClick={async () => {
                try {
                  setLoading(true);
                  await Promise.all(
                    unmappedCodeGroupKeys.map((codeGroupKey) =>
                      Api.CodeGroup.removePolicyCodeGroupMapping(
                        policyState?.policyKey,
                        codeGroupKey
                      )
                    )
                  );
                  dispatch(
                    setDialog({
                      title: "Success",
                      message: "Unmapped code groups have been removed.",
                    })
                  );
                  closeRuleUnitValidationDialog();
                } catch (error) {
                  const message =
                    formErrorStrfromObj(error) ||
                    "Unable to remove one or more code groups.";
                  dispatch(setDialog({ message, title: "Error" }));
                } finally {
                  setLoading(false);
                }
              }}
            >
              REMOVE CODE GROUP
            </Button>
          )}
          {unmappedCodeGroupKeys.length > 0 && (
            <Button variant="outlined" onClick={closeRuleUnitValidationDialog}>
              UPDATE RULES
            </Button>
          )}
          <Button variant="text" onClick={closeRuleUnitValidationDialog}>
            CLOSE
          </Button>
        </DialogActions>
      </Dialog>
      <ValidationErrorsDialog
        open={validationDialogOpen}
        errors={validationErrors}
        onClose={() => setValidationDialogOpen(false)}
      />
    </Box>
  );
};
export default PolicyTranslationPostOHR;
